import { useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { formatAUD, getFinancialYear, getFinancialYearStart, getFinancialYearEnd } from '@/lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import {
  PieChart, Pie, Cell,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  ArrowLeft, Download, TrendingUp, TrendingDown,
  Calendar, Award, Flame, Wallet, Receipt, Star,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

const CHART_COLORS = [
  'hsl(168, 55%, 38%)', 'hsl(43, 90%, 55%)', 'hsl(0, 72%, 51%)',
  'hsl(200, 60%, 50%)', 'hsl(280, 50%, 55%)', 'hsl(152, 60%, 42%)',
  'hsl(30, 80%, 55%)', 'hsl(340, 60%, 50%)', 'hsl(190, 70%, 45%)',
  'hsl(60, 70%, 50%)',
];

function getFYOptions() {
  const now = new Date();
  const startYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  const options: { label: string; startYear: number }[] = [];
  for (let i = 0; i < 5; i++) {
    const y = startYear - i;
    options.push({ label: `${y}-${(y + 1).toString().slice(-2)}`, startYear: y });
  }
  return options;
}

export default function YearInReviewPage() {
  const { data } = useAuth();
  const navigate = useNavigate();
  const fyOptions = useMemo(() => getFYOptions(), []);
  const [selectedFY, setSelectedFY] = useState(fyOptions[0].label);

  const selectedStartYear = fyOptions.find(o => o.label === selectedFY)!.startYear;
  const fyStart = new Date(selectedStartYear, 6, 1);
  const fyEnd = new Date(selectedStartYear + 1, 5, 30);

  const stats = useMemo(() => {
    const txns = data.transactions.filter(t => {
      const d = new Date(t.date);
      return d >= fyStart && d <= fyEnd;
    });

    const totalIncome = txns.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    const totalExpenses = txns.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
    const totalSuper = txns.filter(t => t.type === 'income' && t.superContribution).reduce((s, t) => s + (t.superContribution ?? 0), 0);
    const deductibleTotal = txns.filter(t => t.taxDeductible).reduce((s, t) => s + t.amount, 0);
    const savingsRate = totalIncome > 0 ? ((totalIncome - totalExpenses) / totalIncome) * 100 : 0;

    // Monthly breakdown
    const monthly: Record<string, { month: string; income: number; expenses: number }> = {};
    for (let m = 0; m < 12; m++) {
      const d = new Date(selectedStartYear, 6 + m, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthly[key] = { month: key, income: 0, expenses: 0 };
    }
    txns.forEach(t => {
      const key = t.date.slice(0, 7);
      if (monthly[key]) {
        if (t.type === 'income') monthly[key].income += t.amount;
        else monthly[key].expenses += t.amount;
      }
    });
    const monthlyData = Object.values(monthly).map(m => ({
      ...m,
      label: new Date(m.month + '-01').toLocaleDateString('en-AU', { month: 'short' }),
      net: m.income - m.expenses,
    }));

    // Category spending
    const catMap: Record<string, number> = {};
    txns.filter(t => t.type === 'expense').forEach(t => {
      catMap[t.category] = (catMap[t.category] || 0) + t.amount;
    });
    const spendingByCategory = Object.entries(catMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // Deductibles by category
    const deductMap: Record<string, { total: number; count: number }> = {};
    txns.filter(t => t.taxDeductible).forEach(t => {
      if (!deductMap[t.category]) deductMap[t.category] = { total: 0, count: 0 };
      deductMap[t.category].total += t.amount;
      deductMap[t.category].count += 1;
    });
    const deductibleCategories = Object.entries(deductMap).sort((a, b) => b[1].total - a[1].total);

    // Highlights
    const bestMonth = monthlyData.reduce((best, m) => m.net > best.net ? m : best, monthlyData[0]);
    const worstMonth = monthlyData.reduce((worst, m) => m.net < worst.net ? m : worst, monthlyData[0]);
    const biggestExpenseCategory = spendingByCategory[0];
    const daysTracked = new Set(txns.map(t => t.date)).size;

    // Bills paid
    const billsPaid = data.bills.filter(b => b.paid && b.lastPaidDate && new Date(b.lastPaidDate) >= fyStart && new Date(b.lastPaidDate) <= fyEnd).length;

    return {
      totalIncome, totalExpenses, totalSuper, deductibleTotal, savingsRate,
      monthlyData, spendingByCategory, deductibleCategories,
      bestMonth, worstMonth, biggestExpenseCategory,
      daysTracked, txnCount: txns.length, billsPaid,
    };
  }, [data.transactions, data.bills, fyStart, fyEnd, selectedStartYear]);

  const chartConfig = {
    income: { label: 'Income', color: 'hsl(152, 60%, 42%)' },
    expenses: { label: 'Expenses', color: 'hsl(0, 72%, 51%)' },
  };

  async function generatePDF() {
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const doc = new jsPDF();
    const fyLabel = `FY ${selectedFY}`;
    const period = `1 July ${selectedStartYear} – 30 June ${selectedStartYear + 1}`;

    doc.setFontSize(22);
    doc.text('Year in Review', 14, 22);
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text(`${fyLabel}  •  ${period}`, 14, 30);
    doc.text(`Generated ${new Date().toLocaleDateString('en-AU')}`, 14, 37);

    // Key stats
    doc.setTextColor(0);
    doc.setFontSize(14);
    doc.text('Key Statistics', 14, 52);

    autoTable(doc, {
      startY: 56,
      head: [['Metric', 'Value']],
      body: [
        ['Total Income', formatAUD(stats.totalIncome)],
        ['Total Expenses', formatAUD(stats.totalExpenses)],
        ['Net Savings', formatAUD(stats.totalIncome - stats.totalExpenses)],
        ['Savings Rate', `${stats.savingsRate.toFixed(1)}%`],
        ['Super Contributions', formatAUD(stats.totalSuper)],
        ['Tax Deductions', formatAUD(stats.deductibleTotal)],
        ['Transactions Recorded', stats.txnCount.toString()],
        ['Days Tracked', stats.daysTracked.toString()],
      ],
      theme: 'striped',
      headStyles: { fillColor: [45, 140, 120] },
    });

    // Monthly breakdown
    let y = (doc as any).lastAutoTable.finalY + 12;
    doc.setFontSize(14);
    doc.text('Monthly Breakdown', 14, y);

    autoTable(doc, {
      startY: y + 4,
      head: [['Month', 'Income', 'Expenses', 'Net']],
      body: stats.monthlyData.map(m => [
        m.label,
        formatAUD(m.income),
        formatAUD(m.expenses),
        formatAUD(m.net),
      ]),
      theme: 'striped',
      headStyles: { fillColor: [45, 140, 120] },
    });

    // Spending by category
    y = (doc as any).lastAutoTable.finalY + 12;
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setFontSize(14);
    doc.text('Spending by Category', 14, y);

    if (stats.spendingByCategory.length > 0) {
      autoTable(doc, {
        startY: y + 4,
        head: [['Category', 'Amount', '% of Total']],
        body: stats.spendingByCategory.map(c => [
          c.name,
          formatAUD(c.value),
          stats.totalExpenses > 0 ? `${((c.value / stats.totalExpenses) * 100).toFixed(1)}%` : '0%',
        ]),
        theme: 'striped',
        headStyles: { fillColor: [45, 140, 120] },
      });
    }

    // Tax deductions
    y = (doc as any).lastAutoTable?.finalY + 12 || y + 12;
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setFontSize(14);
    doc.text('Tax-Deductible Expenses', 14, y);

    if (stats.deductibleCategories.length > 0) {
      autoTable(doc, {
        startY: y + 4,
        head: [['Category', 'Items', 'Total']],
        body: stats.deductibleCategories.map(([cat, v]) => [cat, v.count.toString(), formatAUD(v.total)]),
        foot: [['Total', '', formatAUD(stats.deductibleTotal)]],
        theme: 'striped',
        headStyles: { fillColor: [45, 140, 120] },
        footStyles: { fillColor: [230, 245, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
      });
    } else {
      doc.setFontSize(11);
      doc.text('No tax-deductible expenses recorded.', 14, y + 8);
    }

    // Disclaimer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setTextColor(130);
    doc.text('This report is for informational purposes only. Please consult a registered tax agent.', 14, pageHeight - 10);

    doc.save(`AussiBooks_Year_In_Review_FY${selectedFY}.pdf`);
  }

  return (
    <div className="px-4 pt-6 pb-24">
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold">Year in Review</h1>
          <p className="text-sm text-muted-foreground">Your complete financial year summary</p>
        </div>
        <Select value={selectedFY} onValueChange={setSelectedFY}>
          <SelectTrigger className="w-[110px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {fyOptions.map(o => (
              <SelectItem key={o.label} value={o.label}>FY {o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Hero stats */}
      <div className="mb-4 grid grid-cols-2 gap-2">
        <Card className="bg-success/5 border-success/20">
          <CardContent className="p-3 text-center">
            <TrendingUp className="mx-auto h-5 w-5 text-success mb-1" />
            <p className="text-xs text-muted-foreground">Income</p>
            <p className="font-display text-lg font-bold text-success">{formatAUD(stats.totalIncome)}</p>
          </CardContent>
        </Card>
        <Card className="bg-destructive/5 border-destructive/20">
          <CardContent className="p-3 text-center">
            <TrendingDown className="mx-auto h-5 w-5 text-destructive mb-1" />
            <p className="text-xs text-muted-foreground">Expenses</p>
            <p className="font-display text-lg font-bold text-destructive">{formatAUD(stats.totalExpenses)}</p>
          </CardContent>
        </Card>
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-3 text-center">
            <Wallet className="mx-auto h-5 w-5 text-primary mb-1" />
            <p className="text-xs text-muted-foreground">Net Savings</p>
            <p className={cn('font-display text-lg font-bold', stats.totalIncome - stats.totalExpenses >= 0 ? 'text-success' : 'text-destructive')}>
              {formatAUD(stats.totalIncome - stats.totalExpenses)}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-accent/30 border-accent/40">
          <CardContent className="p-3 text-center">
            <Star className="mx-auto h-5 w-5 text-accent-foreground mb-1" />
            <p className="text-xs text-muted-foreground">Savings Rate</p>
            <p className="font-display text-lg font-bold">{stats.savingsRate.toFixed(1)}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Highlights */}
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Award className="h-4 w-4 text-primary" />
            Year Highlights
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Transactions recorded</span>
            <span className="font-semibold">{stats.txnCount}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Days tracked</span>
            <span className="font-semibold">{stats.daysTracked}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Bills paid</span>
            <span className="font-semibold">{stats.billsPaid}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Super contributions</span>
            <span className="font-semibold">{formatAUD(stats.totalSuper)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Tax deductions</span>
            <span className="font-semibold text-primary">{formatAUD(stats.deductibleTotal)}</span>
          </div>
          {stats.biggestExpenseCategory && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Biggest category</span>
              <span className="font-semibold">{stats.biggestExpenseCategory.name}</span>
            </div>
          )}
          {stats.bestMonth && stats.monthlyData.length > 1 && (
            <>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-1"><Flame className="h-3 w-3 text-success" /> Best month</span>
                <span className="font-semibold text-success">{stats.bestMonth.label} ({formatAUD(stats.bestMonth.net)})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-1"><TrendingDown className="h-3 w-3 text-destructive" /> Toughest month</span>
                <span className="font-semibold text-destructive">{stats.worstMonth.label} ({formatAUD(stats.worstMonth.net)})</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Charts / Table tabs */}
      <Tabs defaultValue="monthly" className="mb-4">
        <TabsList className="w-full mb-3">
          <TabsTrigger value="monthly" className="flex-1">Monthly</TabsTrigger>
          <TabsTrigger value="categories" className="flex-1">Categories</TabsTrigger>
          <TabsTrigger value="tax" className="flex-1">Tax</TabsTrigger>
        </TabsList>

        {/* Monthly income vs expenses */}
        <TabsContent value="monthly">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Monthly Income vs Expenses</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.monthlyData.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No data</p>
              ) : (
                <ChartContainer config={chartConfig} className="h-[250px] w-full">
                  <BarChart data={stats.monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border/40" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} width={45} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="income" fill="var(--color-income)" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="expenses" fill="var(--color-expenses)" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ChartContainer>
              )}

              {/* Monthly table */}
              <div className="mt-4 max-h-[300px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="sticky top-0 bg-background">Month</TableHead>
                      <TableHead className="sticky top-0 bg-background text-right">Income</TableHead>
                      <TableHead className="sticky top-0 bg-background text-right">Expenses</TableHead>
                      <TableHead className="sticky top-0 bg-background text-right">Net</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.monthlyData.map(m => (
                      <TableRow key={m.month}>
                        <TableCell className="text-xs">{m.label}</TableCell>
                        <TableCell className="text-xs text-right font-mono text-success">{formatAUD(m.income)}</TableCell>
                        <TableCell className="text-xs text-right font-mono text-destructive">{formatAUD(m.expenses)}</TableCell>
                        <TableCell className={cn('text-xs text-right font-mono font-semibold', m.net >= 0 ? 'text-success' : 'text-destructive')}>
                          {formatAUD(m.net)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="border-t-2">
                      <TableCell className="text-xs font-bold">Total</TableCell>
                      <TableCell className="text-xs text-right font-mono font-bold text-success">{formatAUD(stats.totalIncome)}</TableCell>
                      <TableCell className="text-xs text-right font-mono font-bold text-destructive">{formatAUD(stats.totalExpenses)}</TableCell>
                      <TableCell className={cn('text-xs text-right font-mono font-bold', stats.totalIncome - stats.totalExpenses >= 0 ? 'text-success' : 'text-destructive')}>
                        {formatAUD(stats.totalIncome - stats.totalExpenses)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories pie */}
        <TabsContent value="categories">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Spending by Category</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.spendingByCategory.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No expenses</p>
              ) : (
                <>
                  <ChartContainer config={{}} className="mx-auto aspect-square max-h-[220px]">
                    <PieChart>
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Pie data={stats.spendingByCategory} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={85} paddingAngle={2}>
                        {stats.spendingByCategory.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ChartContainer>

                  <div className="mt-3 space-y-1.5">
                    {stats.spendingByCategory.map((c, i) => (
                      <div key={c.name} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <div className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                          <span className="text-foreground">{c.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">
                            {stats.totalExpenses > 0 ? `${((c.value / stats.totalExpenses) * 100).toFixed(1)}%` : '—'}
                          </span>
                          <span className="font-mono font-medium">{formatAUD(c.value)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tax deductions */}
        <TabsContent value="tax">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Receipt className="h-4 w-4" />
                Tax Deductions Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats.deductibleCategories.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No tax-deductible expenses</p>
              ) : (
                <div className="space-y-2">
                  {stats.deductibleCategories.map(([cat, v]) => (
                    <div key={cat} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                      <div>
                        <p className="text-sm font-medium">{cat}</p>
                        <p className="text-xs text-muted-foreground">{v.count} item{v.count !== 1 ? 's' : ''}</p>
                      </div>
                      <Badge variant="secondary" className="font-display font-semibold">{formatAUD(v.total)}</Badge>
                    </div>
                  ))}
                  <Separator />
                  <div className="flex justify-between font-display font-bold text-sm">
                    <span>Total Deductible</span>
                    <span className="text-primary">{formatAUD(stats.deductibleTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Super Contributions</span>
                    <span className="font-semibold">{formatAUD(stats.totalSuper)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* PDF Export */}
      <Card className="border-dashed border-primary/30 bg-primary/5">
        <CardContent className="p-4 text-center">
          <p className="mb-3 text-sm text-muted-foreground">
            Download a comprehensive PDF with all your annual stats, breakdowns, and tax deductions.
          </p>
          <Button className="gap-2" onClick={generatePDF}>
            <Download className="h-4 w-4" />
            Download Year in Review PDF
          </Button>
        </CardContent>
      </Card>

      <p className="mt-3 text-xs text-muted-foreground text-center">
        This report is for informational purposes only. Please consult a registered tax agent for professional advice.
      </p>
    </div>
  );
}
